import React, { useCallback, useContext, useState } from "react";

import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import { ICorporateContextProps } from "contexts/CorporateContext/CorporateContextInterfaces";
import { UserContext } from "contexts/UserContext/UserContext";
import { UsersEntity } from "entities/Users/UsersEntity";

/**
 * CorporateContext for the API Administrator portal (OS-2557).
 *
 * This is the whole point of running the portal as its own route subtree: the standard
 * CorporateContextProvider fires getCorporateDivisionsRegionsMills unconditionally, which reads
 * corporate/division/region/mill. Granting the api_administrator role those tables would widen it
 * well past "manages API keys". Supplying the context shape directly instead means the org tree is
 * never queried, so the role's Hasura surface stays at the eight tables in backend/hasura/metadata.
 *
 * Everything downstream (useRolesPermissions, useAllowApiKeyManagement, APIKeyManagement) reads
 * CorporateContext and therefore works unchanged — only currentUser is genuinely needed, and it
 * comes from the mill-free getApiAdminProfile document.
 */
export const ApiAdminContextProvider: React.FC<ICorporateContextProps> = ({ children }) => {
    const [isFullscreenActive, setIsFullscreenActive] = useState(false);
    const { user_id } = useContext(UserContext);
    const { currentUser, loading: isLoadingCurrentUser, refetch } = UsersEntity.GetApiAdminProfile(user_id || 0);

    const refreshCurrentUser = useCallback(() => {
        refetch().catch(() => undefined);
    }, [refetch]);

    const handleSetIsFullscreenActive = (value: boolean) => {
        setIsFullscreenActive(value);
    };

    const providerValue = {
        // No mill notification settings for this role — it is not an operational recipient.
        enabledNotifications: false,
        enabledNotificationsIds: [],
        // No Birch/Aspen switching: the portal is a single page.
        rcaEnabled: false,
        isFullscreenActive,
        handleSetIsFullscreenActive,
        // The org tree is deliberately never fetched (see the note above).
        divisions: [],
        corporations: [],
        millsWithNoDivision: [],
        isLoadingCorporationsAndDivisions: false,
        currentUser,
        isLoadingCurrentUser,
        refreshCorporationsAndDivisions: () => Promise.resolve({} as any),
        refreshCurrentUser,
    };

    return <CorporateContext.Provider value={providerValue}>{children}</CorporateContext.Provider>;
};
